import React, { lazy, useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

// project imports
import paths from 'paths';
import useAuth from 'hooks/useAuth';
import { useBackendUserState } from 'contexts/UserContext';
import { useMutation } from '@apollo/client';
import { USER_TOKEN_MUTATION } from 'queries/account/mutation';
import { useDispatch } from 'react-redux';
import { SNACKBAR_OPEN } from 'store/actions';
import MainLayout from '../layout/MainLayout';
import Loadable from '../ui-component/Loadable';
import AuthGuard from '../utils/route-guard/AuthGuard';

// sample page routing
const DashboardPage = Loadable(lazy(() => import('views/dashboard')));

const UserPage = Loadable(lazy(() => import('views/user')));

const RotationsListViewPage = Loadable(lazy(() => import('views/rotations/ListView')));
const RotationDetailsPage = Loadable(lazy(() => import('views/rotations/DetailView')));

// const DatabasePage = Loadable(lazy(() => import('views/database')));
const DatabasePreceptorListPage = Loadable(lazy(() => import('views/database/PreceptorListView')));
const DatabaseStudentListPage = Loadable(lazy(() => import('views/database/StudentListView')));
const DatabaseStudentPage = Loadable(lazy(() => import('views/database/StudentView')));
const DatabasePreceptorPage = Loadable(lazy(() => import('views/database/PreceptorView')));

const RequestsPage = Loadable(lazy(() => import('views/requests/ListView')));
const RequestPage = Loadable(lazy(() => import('views/requests/DetailView')));

const ReportsPage = Loadable(lazy(() => import('views/reports')));

const RemindersPage = Loadable(lazy(() => import('views/reminders')));

const NotificationsPage = Loadable(lazy(() => import('views/notifications')));

const AnalitycsPage = Loadable(lazy(() => import('views/analytics')));
const AnalyticsRotation = Loadable(lazy(() => import('views/analyticsRotation')));
const AnalyticsPreceptor = Loadable(lazy(() => import('views/analyticsPreceptors')));
const AnalyticsPipeline = Loadable(lazy(() => import('views/analyticsPipeline')));
const AnalyticsCCTime = Loadable(lazy(() => import('views/analyticsCCTime')));

const SettingsGeneralPage = Loadable(lazy(() => import('views/settings/general/')));

const SettingsTagsPage = Loadable(lazy(() => import('views/settings/tags')));

const SettingsStateOwnershipPage = Loadable(lazy(() => import('views/settings/stateOwnership')));

const AdminProfilePage = Loadable(lazy(() => import('views/database/AdminView')));
const SchoolListViewPage = Loadable(lazy(() => import('views/school/ListView')));
const SchoolDetailsPage = Loadable(lazy(() => import('views/school/DetailView')));

const AISuggection = Loadable(lazy(() => import('views/ai_suggestion')));

const CCListViewPage = Loadable(lazy(() => import('views/database/CCListView')));
const CCViewPage = Loadable(lazy(() => import('views/database/CCView')));

const CCListViewPageSchool = Loadable(lazy(() => import('views/school/ListViewSchool')));
const CCViewPageSchool = Loadable(lazy(() => import('views/school/DetailViewSchool')));
const CCListViewPageRotation = Loadable(lazy(() => import('views/rotations/ListViewSchool')));
const CCViewPageRotation = Loadable(lazy(() => import('views/rotations/DetailViewSchool')));
const CCListViewPageStudent = Loadable(lazy(() => import('views/database/StudentListViewSchool')));
const CCViewPageStudent = Loadable(lazy(() => import('views/database/StudentViewSchool')));
const CCListViewPagePreceptor = Loadable(lazy(() => import('views/database/PreceptorListViewSchool')));
const CCViewPagePreceptor = Loadable(lazy(() => import('views/database/PreceptorViewSchool')));

const Whitelists = Loadable(lazy(() => import('views/whitelist/ListView')));
const Whitelist = Loadable(lazy(() => import('views/whitelist/DetailView')));

// -----------------------|| MAIN ROUTING ||-----------------------//

const MainRoutes = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [token, setToken] = useState(null);
  const {
    requestPermission,
    onMessageListener,
  } = useAuth();
  const { state: { user: backendUser } } = useBackendUserState();

  const [saveToken] = useMutation(USER_TOKEN_MUTATION);

  useEffect(() => {
    async function fetchToken() {
      const tokenValue = await requestPermission();
      setToken(tokenValue);
    }
    fetchToken();

    onMessageListener().then(payload => {
      dispatch({
        type: SNACKBAR_OPEN,
        open: true,
        variant: 'alert',
        alertSeverity: 'success',
        message: payload.notification.body,
      });
    }).catch(() => '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token && token !== '' && backendUser?.uuid) {
      saveToken({ variables: { token, uuid: backendUser.uuid } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUser, token]);

  return (
    <Route
      path={[
        paths.dashboard.pattern,

        paths.user.pattern,

        paths.rotations.pattern,
        paths.rotationDetails.pattern,
        paths.rotationsArchive.pattern,
        paths.rotationDetailsArchive.pattern,

        // paths.database.pattern,
        paths.databaseStudentView.pattern,
        paths.databasePreceptorView.pattern,
        paths.preceptors.pattern,
        paths.students.pattern,

        paths.preceptorsOld.pattern,
        paths.studentsOld.pattern,
        paths.databaseStudentViewOld.pattern,
        paths.databasePreceptorViewOld.pattern,

        paths.clinicCoordinators.pattern,
        paths.clinicCoordinatorView.pattern,

        paths.requests.pattern,
        paths.request.pattern,

        paths.reports.pattern,

        paths.reminders.pattern,

        paths.settingsGeneral.pattern,

        paths.settingsTags.pattern,

        paths.settingsStateOwnership.pattern,

        paths.adminProfile.pattern,
        paths.notifications.pattern,
        paths.analytics.pattern,
        paths.analyticsRotation.pattern,
        paths.analyticsPreceptor.pattern,
        paths.analyticsPipeline.pattern,
        paths.analyticsCCTime.pattern,
        paths.suggestion.pattern,

        paths.school.pattern,

        paths.schoolCCSchool.pattern,
        paths.schoolCCSchoolDetails.pattern,
        paths.rotationsCCSchool.pattern,
        paths.rotationsCCSchoolDetails.pattern,
        paths.studentsCCSchool.pattern,
        paths.studentsCCSchoolDetails.pattern,
        paths.preceptorsCCSchool.pattern,
        paths.preceptorsCCSchoolDetails.pattern,

        paths.studentsSchool.pattern,
        paths.rotationsSchool.pattern,

        paths.studentsArchive.pattern,
        paths.preceptorsArchive.pattern,
        paths.databasePreceptorViewArchive.pattern,
        paths.databaseStudentViewArchive.pattern,

        paths.whitelists.pattern,
        paths.whitelist.pattern,
      ]}
    >
      <MainLayout showBreadcrumb>
        <Switch location={location} key={location.pathname}>
          <AuthGuard>
            <Route path={paths.dashboard.pattern} exact component={DashboardPage} />

            <Route path={paths.user.pattern} exact component={UserPage} />

            <Route path={paths.rotations.pattern} exact component={RotationsListViewPage} />
            <Route path={paths.rotationDetails.pattern} exact component={RotationDetailsPage} />
            <Route path={paths.rotationsArchive.pattern} exact component={RotationsListViewPage} />
            <Route path={paths.rotationDetailsArchive.pattern} exact component={RotationDetailsPage} />

            {/* <Route path={paths.database.pattern} exact component={DatabasePage} /> */}
            <Route path={paths.preceptorsOld.pattern} exact component={DatabasePreceptorListPage} />
            <Route path={paths.studentsOld.pattern} exact component={DatabaseStudentListPage} />
            <Route path={paths.preceptors.pattern} exact component={DatabasePreceptorListPage} />
            <Route path={paths.students.pattern} exact component={DatabaseStudentListPage} />
            <Route path={paths.databaseStudentViewOld.pattern} exact component={DatabaseStudentPage} />
            <Route path={paths.databasePreceptorViewOld.pattern} exact component={DatabasePreceptorPage} />
            <Route path={paths.databaseStudentView.pattern} exact component={DatabaseStudentPage} />
            <Route path={paths.databasePreceptorView.pattern} exact component={DatabasePreceptorPage} />

            <Route path={paths.clinicCoordinators.pattern} exact component={CCListViewPage} />
            <Route path={paths.clinicCoordinatorView.pattern} exact component={CCViewPage} />

            <Route path={paths.requests.pattern} exact component={RequestsPage} />
            <Route path={paths.request.pattern} exact component={RequestPage} />

            <Route path={paths.reports.pattern} exact component={ReportsPage} />

            <Route path={paths.reminders.pattern} exact component={RemindersPage} />

            <Route path={paths.settingsGeneral.pattern} exact component={SettingsGeneralPage} />

            <Route path={paths.settingsTags.pattern} exact component={SettingsTagsPage} />

            <Route path={paths.settingsStateOwnership.pattern} exact component={SettingsStateOwnershipPage} />

            <Route path={paths.adminProfile.pattern} exact component={AdminProfilePage} />

            <Route path={paths.suggestion.pattern} exact component={AISuggection} />

            <Route path={paths.notifications.pattern} exact component={NotificationsPage} />
            <Route path={paths.analytics.pattern} exact component={AnalitycsPage} />
            <Route path={paths.analyticsRotation.pattern} exact component={AnalyticsRotation} />
            <Route path={paths.analyticsPreceptor.pattern} exact component={AnalyticsPreceptor} />
            <Route path={paths.analyticsPipeline.pattern} exact component={AnalyticsPipeline} />
            <Route path={paths.analyticsCCTime.pattern} exact component={AnalyticsCCTime} />
            <Route path={paths.school.pattern} exact component={SchoolListViewPage} />
            <Route path={paths.schoolDetails.pattern} exact component={SchoolDetailsPage} />

            {/* School admin routes */}
            <Route path={paths.schoolCCSchool.pattern} exact component={CCListViewPageSchool} />
            <Route path={paths.schoolCCSchoolDetails.pattern} exact component={CCViewPageSchool} />
            <Route path={paths.rotationsCCSchool.pattern} exact component={CCListViewPageRotation} />
            <Route path={paths.rotationsCCSchoolDetails.pattern} exact component={CCViewPageRotation} />
            <Route path={paths.studentsCCSchool.pattern} exact component={CCListViewPageStudent} />
            <Route path={paths.studentsCCSchoolDetails.pattern} exact component={CCViewPageStudent} />
            <Route path={paths.preceptorsCCSchool.pattern} exact component={CCListViewPagePreceptor} />
            <Route path={paths.preceptorsCCSchoolDetails.pattern} exact component={CCViewPagePreceptor} />

            <Route path={paths.studentsSchool.pattern} exact component={DatabaseStudentListPage} />
            <Route path={paths.rotationsSchool.pattern} exact component={RotationsListViewPage} />

            <Route path={paths.preceptorsArchive.pattern} exact component={DatabasePreceptorListPage} />
            <Route path={paths.studentsArchive.pattern} exact component={DatabaseStudentListPage} />
            <Route path={paths.databaseStudentViewArchive.pattern} exact component={DatabaseStudentPage} />
            <Route path={paths.databasePreceptorViewArchive.pattern} exact component={DatabasePreceptorPage} />

            <Route path={paths.whitelists.pattern} exact component={Whitelists} />
            <Route path={paths.whitelist.pattern} exact component={Whitelist} />
          </AuthGuard>
        </Switch>
      </MainLayout>
    </Route>
  );
};

export default MainRoutes;
